import React from "react";
import { Row, Col, Label, FormGroup } from "reactstrap";
import { AppCard } from "../../components/ui";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "../../models/SelectItem";
import { Button } from "primereact/button";
import { CreditCard, Check } from "lucide-react";
import { merchantPaymentMethodApiService } from "../../services/api/merchants/MerchantPaymentMethodApiService";
import { storageService } from "../../services/StorageService";
import { MerchantPaymentMethod } from "../../models/MerchantModel";
import { Wallets, NetworkType, PaymentMethodType } from '../../models/FinancialData';
import { DepositMoneyFromExistingCardPaymentMethodUserRequest, PaymentMethodStatus, PaymentType } from "../../models/MoneyTransferModal";
import { CalculateMoneyTransferChargeRequest } from "../../models/MoneyTransferModal";
import { merchantPaymentApiService } from "../../services/api/merchants/MerchantPaymentApiService";
import { httpService } from "../../services/HttpService";
import { InputNumber } from "primereact/inputnumber";
import { toastService } from "../../services/toastService";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel, TabViewTabChangeEvent } from "primereact/tabview";
import { merchantWalletApiService } from "../../services/api/merchants/MerchantWalletApiService";
import PaymentModaliCard from "./PaymentModaliCard";
import { ConfirmDepositPaymentDialog } from "../../components/ConfirmDepositPaymentDialog";
import { operatorsApiService } from "../../services/api/users/OperatorApiService";
import { OperatorDataModel } from "../../models/User";

interface DepositPaymentDetails {
    destinationCustomerAccount: string;
    currency: string;
    transactionAmount: number;
    transactionFee: number;
    paymentMethods: MerchantPaymentMethod;
}

interface DepositFromPaymentMethodsProps {
    OnSuccess: (amount: number, selectedItem: MerchantPaymentMethod | undefined) => void;
    onHide: () => void;
}

interface DepositFromPaymentMethodsState {
    amount: number;
    selectedPaymentMethod: MerchantPaymentMethod | undefined;
    paymentMethods: MerchantPaymentMethod[];
    selectedWallet: Wallets | undefined;
    wallets: Wallets[];
    sourceAccountsOptions: SelectItem[];
    isLoading: boolean;
    isSubmitting: boolean;
    showiCardPaymentForm: boolean;
    showiConfirmDialog: boolean;
    icardPaymentToken: string;
    icardDomainUrl: string;
    userIp: string;
    loggedUserData: OperatorDataModel | null;
    selectedPaymentMethodType: SelectedPaymentMethodEnum;
    confirmDepositPaymentDetails: DepositPaymentDetails | null;
}

enum SelectedPaymentMethodEnum {
    ExistingPaymentMethodCard,
    NewCard
}

class DepositFromPaymentMethods extends React.Component<DepositFromPaymentMethodsProps, DepositFromPaymentMethodsState> {
    private t: (key: string) => string = (key: string) => key;

    constructor(props: DepositFromPaymentMethodsProps) {
        super(props);
        this.state = {
            amount: 0,
            selectedPaymentMethod: undefined,
            paymentMethods: [],
            wallets: [],
            sourceAccountsOptions: [],
            isLoading: true,
            isSubmitting: false,
            showiCardPaymentForm: false,
            showiConfirmDialog: false,
            icardPaymentToken: "",
            icardDomainUrl: "",
            userIp: "",
            loggedUserData: null,
            selectedPaymentMethodType: SelectedPaymentMethodEnum.NewCard,
            selectedWallet: undefined,
            confirmDepositPaymentDetails: null
        };
    }

    componentDidMount(): void {
        this.loadData();
    }

    loadData = async () => {
        await Promise.all([this.getWallets(), this.getPaymentMethods(), this.getWallets(), this.checkUserIp(), this.updateUserData()]);
        this.setState({ isLoading: false });
    };

    getWallets = async () => {
        const merchantId = storageService.getLoggedInUserMerchantId();
        const walletsResult = await merchantWalletApiService.getWallets(merchantId);
        if (!walletsResult.success) {
            this.setState({ isLoading: false });
            toastService.error(this.t(walletsResult.message));
            return;
        }

        const bgnWallets = walletsResult.data.filter(wallet => wallet.currency === "BGN");
        this.setState({ 
            wallets: bgnWallets,
            selectedWallet: bgnWallets.length > 0 ? bgnWallets[0] : undefined
        });
    };

    getPaymentMethods = async () => {
        const merchantId = storageService.getLoggedInUserMerchantId();
        const paymentMethods = await merchantPaymentMethodApiService.getAll(merchantId);
        if (!paymentMethods.success) {
            this.setState({ isLoading: false });
            toastService.error(this.t(paymentMethods.message));
            return;
        }

        const paymentMethodsCard = paymentMethods.data.filter((a) => a.paymentType.toLowerCase() == PaymentMethodType.Card.toLowerCase());

        this.setState({ 
            paymentMethods: paymentMethodsCard,
            selectedPaymentMethod: paymentMethodsCard.length > 0 ? paymentMethodsCard[0] : undefined
        });
    };

    checkUserIp = async (): Promise<string> => {
        if (this.state.userIp && this.state.userIp.length) {
            return this.state.userIp;
        }

        const ipData = await fetch("https://ifconfig.me/all.json")
            .then((response) => response.json())
            .catch((e) => {
                return false;
            });

        const ipAddress = ipData.ip_addr;
        this.setState({ userIp: ipAddress });
        return ipAddress;
    };

    updateUserData = async () => {
        const userData = await operatorsApiService.get();
        if (!userData.success) {
            return;
        }
        this.setState({ loggedUserData: userData.data });
    };
        
    onSubmitIcard = async () => {
        this.setState({ isSubmitting: true });
        console.log("Is Submitting");
        if (this.state.selectedPaymentMethodType === SelectedPaymentMethodEnum.NewCard) {
            console.log("Is Submitting => New Card");
            try {
                const merchantId = storageService.getLoggedInUserMerchantId();
                const selectedWallet = this.state.wallets.find((a) => a.ibanAccountNumber === this.state.confirmDepositPaymentDetails?.destinationCustomerAccount);

                const depositResult = await merchantPaymentApiService.depositMoneyFromNewCardAsync(merchantId, {
                    transactionAmount: parseFloat(this.state.confirmDepositPaymentDetails?.transactionAmount?.toString() || "0"),
                    transactionCharge: this.state.confirmDepositPaymentDetails?.transactionFee || 0,
                    paymentMethodId: "0",
                    transactionCurrency: this.state.confirmDepositPaymentDetails?.currency,
                    accountNumber: this.state.selectedWallet?.ibanAccountNumber,
                    deviceIP: this.state.userIp
                } as DepositMoneyFromExistingCardPaymentMethodUserRequest);

                if (!depositResult.success) {
                    this.setState({ isSubmitting: false });
                    toastService.error(this.t(depositResult.message));
                    return;
                }

                console.log("Redirect to URL", depositResult.data);

                this.setState({ 
                    confirmDepositPaymentDetails: null,
                    icardPaymentToken: depositResult.data.data,
                    icardDomainUrl: depositResult.data.redirectUrl,
                    isSubmitting: false
                });

                setTimeout(() => {
                    this.setState({ showiCardPaymentForm: true });
                }, 1000);
            } catch (error) {
                console.error("Error retrieving redirect URL:", error);
                this.setState({ isSubmitting: false });
                toastService.error("An error occurred while processing your request");
            }
        } else {
            try {
                const selectedWallet = this.state.wallets.find((a) => a.ibanAccountNumber === this.state.confirmDepositPaymentDetails?.destinationCustomerAccount);
                const merchantId = storageService.getLoggedInUserMerchantId();
                const depositResult = await merchantPaymentApiService.depositMoneyFromPaymentMethod3DAsync(merchantId, {
                    transactionAmount: parseFloat(this.state.confirmDepositPaymentDetails?.transactionAmount?.toString() || "0"),
                    transactionCharge: this.state.confirmDepositPaymentDetails?.transactionFee || 0,
                    paymentMethodId: this.state.confirmDepositPaymentDetails?.paymentMethods.value,
                    transactionCurrency: this.state.confirmDepositPaymentDetails?.currency,
                    accountNumber: this.state.selectedWallet?.ibanAccountNumber,
                    deviceIP: this.state.userIp
                } as DepositMoneyFromExistingCardPaymentMethodUserRequest);

                if (!depositResult.success) {
                    this.setState({ isSubmitting: false });
                    toastService.error(this.t(depositResult.message));
                    return;
                }

                this.setState({
                    icardPaymentToken: depositResult.data.data,
                    icardDomainUrl: depositResult.data.redirectUrl,
                    showiCardPaymentForm: true,
                    isSubmitting: false
                });
            } catch (error) {
                console.error("Error processing payment:", error);
                this.setState({ isSubmitting: false });
                toastService.error("An error occurred while processing your payment");
            }
        }
    };

    onDeposit = async () => {
        if (this.state.amount <= 0) {
            toastService.warn(this.t("Please enter a positive amount."));
            return;
        }
        if (
            this.state.selectedPaymentMethodType === SelectedPaymentMethodEnum.ExistingPaymentMethodCard &&
            this.state.selectedPaymentMethod &&
            this.state.selectedPaymentMethod.paymentType.toLowerCase() != PaymentMethodType.Card.toLowerCase()
        ) {
            toastService.info("Services unavailable!");
            return;
        }

        this.setState({ isSubmitting: true });
        const calculateMoneyTransferChargeRequest: CalculateMoneyTransferChargeRequest = {
            customerAccount: this.state.selectedWallet?.ibanAccountNumber || "",
            amount: this.state.amount,
            id: this.state.loggedUserData?.id ?? 0,
            PaymentType:
                this.state.selectedPaymentMethod?.cardType == NetworkType.Visa.toString()
                    ? PaymentType.DepositFromVisaCard
                    : this.state.selectedPaymentMethod?.cardType == NetworkType.MasterCard.toString()
                        ? PaymentType.DepositFromMasterCard
                        : PaymentType.DepositFromOtherCard
        };

        const calculateMoneyTransferChargeResponse = await merchantPaymentApiService.getMoneyTransferCharge(calculateMoneyTransferChargeRequest);

        if (!calculateMoneyTransferChargeResponse.success) {
            this.setState({ isSubmitting: false });
            toastService.error(this.t(calculateMoneyTransferChargeResponse.message));
            return;
        }

        const confirmDepositPayment: DepositPaymentDetails = {
            destinationCustomerAccount: this.state.selectedWallet?.ibanAccountNumber || "",
            currency: "BGN",
            transactionAmount: this.state.amount,
            transactionFee: calculateMoneyTransferChargeResponse.data.transactionFee,
            paymentMethods: this.state.selectedPaymentMethod!
        };

        this.setState({ confirmDepositPaymentDetails: confirmDepositPayment, showiConfirmDialog: true, isSubmitting: false });
    };

    handleTabChange = (event: TabViewTabChangeEvent) => {
        const newType = event.index === 0 
            ? SelectedPaymentMethodEnum.NewCard 
            : SelectedPaymentMethodEnum.ExistingPaymentMethodCard;
        
        this.setState({ 
            selectedPaymentMethodType: newType,
            selectedPaymentMethod: undefined // Reset selected payment method when changing tabs
        });
    };

    tabHeaderTemplate = (options: any) => {
        const isActive = this.state.selectedPaymentMethodType === (options.index === 0 ? SelectedPaymentMethodEnum.NewCard : SelectedPaymentMethodEnum.ExistingPaymentMethodCard);
        return (
            <div
                className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium cursor-pointer transition-colors border-b-2 -mb-px ${isActive ? "border-primary-500 text-primary-600 dark:text-primary-400 bg-white dark:bg-neutral-900" : "border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"}`}
                onClick={options.onClick}
            >
                <span className="font-bold white-space-nowrap">{options.titleElement}</span>
            </div>
        );
    };

    render(): React.ReactNode {
        if (this.state.isLoading) {
            return <div className="loader" />;
        }

        return (
            <Dialog
                header="Deposit from Payment Methods"
                visible={true}
                style={{ width: "70vw" }}
                onHide={this.props.onHide}
                draggable={false}
                resizable={false}
                contentStyle={{ maxHeight: "70vh", overflowY: "auto" }}
            >
                <div>
                    <CreditCard size={80} strokeWidth={1.5} className="text-info block mx-auto" />

                    <AppCard className="mt-4">
                        <TabView 
                            activeIndex={this.state.selectedPaymentMethodType === SelectedPaymentMethodEnum.NewCard ? 0 : 1}
                            onTabChange={this.handleTabChange}
                        >
                            <TabPanel
                                header="New Card"
                                headerTemplate={this.tabHeaderTemplate}
                            >
                                <FormGroup>
                                    <Label>{this.t("Select Account")}</Label>
                                    <Dropdown
                                        value={this.state.selectedWallet?.ibanAccountNumber}
                                        options={this.state.wallets}
                                        onChange={(e) => this.setState({ selectedWallet: this.state.wallets.find(w => w.ibanAccountNumber === e.value) })}
                                        optionLabel="ibanAccountNumber"
                                        optionValue="ibanAccountNumber"
                                        placeholder={this.t("Select an account")}
                                        className="w-100"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>{this.t("Amount")}</Label>
                                    <InputNumber
                                        value={this.state.amount}
                                        onValueChange={(e) => this.setState({ amount: e.value || 0 })}
                                        mode="currency"
                                        currency="BGN"
                                        locale="en-US"
                                        className="w-100"
                                    />
                                </FormGroup>
                            </TabPanel>
                            
                            <TabPanel
                                header="Payment Methods"
                                headerTemplate={this.tabHeaderTemplate}
                            >
                                <FormGroup>
                                    <Label>{this.t("Select Account")}</Label>
                                    <Dropdown
                                        value={this.state.selectedWallet?.ibanAccountNumber}
                                        options={this.state.wallets}
                                        onChange={(e) => this.setState({ selectedWallet: this.state.wallets.find(w => w.ibanAccountNumber === e.value) })}
                                        optionLabel="ibanAccountNumber"
                                        optionValue="ibanAccountNumber"
                                        placeholder={this.t("Select an account")}
                                        className="w-100"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>{this.t("Select Payment Method")}</Label>
                                    <Dropdown
                                        value={this.state.selectedPaymentMethod?.value}
                                        options={this.state.paymentMethods}
                                        onChange={(e: { value: string }) => {
                                            const selectedMethod = this.state.paymentMethods.find(pm => pm.value === e.value);
                                            this.setState({ selectedPaymentMethod: selectedMethod });
                                        }}
                                        optionLabel="alias"
                                        optionValue="value"
                                        itemTemplate={(option) => (
                                            <div className="flex align-items-center">
                                                <div>
                                                    <div className="font-bold">{option.alias}</div>
                                                    <div className="text-sm text-gray-500">Expires: {option.validityMonth}/{option.validityYear}</div>
                                                </div>
                                            </div>
                                        )}
                                        placeholder={this.t("Select a payment method")}
                                        className="w-100"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label>{this.t("Amount")}</Label>
                                    <InputNumber
                                        value={this.state.amount}
                                        onValueChange={(e) => this.setState({ amount: e.value || 0 })}
                                        mode="currency"
                                        currency="BGN"
                                        locale="en-US"
                                        className="w-100"
                                    />
                                </FormGroup>
                            </TabPanel>
                        </TabView>

                        <div className="d-flex justify-content-end mt-4">
                            <Button
                                label={this.t("Deposit")}
                                icon={<Check size={16} />}
                                onClick={this.onDeposit}
                                disabled={this.state.isSubmitting}
                                severity="success"
                            />
                        </div>
                    </AppCard>

                    {this.state.showiCardPaymentForm && (
                        <PaymentModaliCard 
                            visible={this.state.showiCardPaymentForm}
                            onClose={async (message) => {
                                this.setState({
                                    showiCardPaymentForm: false,
                                    icardPaymentToken: "",
                                    icardDomainUrl: ""
                                });
                                
                                if (message === 'ipg.user.close.on.success') {
                                    await this.getPaymentMethods();
                                    this.props.OnSuccess(this.state.amount, this.state.selectedPaymentMethod);
                                }
                            }}
                            domain={this.state.icardDomainUrl}
                            token={this.state.icardPaymentToken}
                            theme="classic"
                        />
                    )}

                    {this.state.showiConfirmDialog && this.state.confirmDepositPaymentDetails && (
                        <ConfirmDepositPaymentDialog
                            visible={true}
                            onHide={() => this.setState({ confirmDepositPaymentDetails: null })}
                            onSubmit={() => {
                                this.setState({ showiConfirmDialog: false });
                                this.onSubmitIcard();
                            }}
                            isSubmitting={this.state.isSubmitting}
                            depositPaymentDetails={this.state.confirmDepositPaymentDetails!}
                        />
                    )}

                </div>
            </Dialog>
        );
    }
}

export default DepositFromPaymentMethods;
